import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { ChevronDown } from 'react-feather';
import { Dropdown } from '@/shared/components/Dropdown';
import type { MenuItemProps } from '@/shared/components/Menu/types';
import { Tooltip } from '@/shared/components/Tooltip';
import { listEligibleTechnicians } from '../../api/listEligibleTechniciansApiService';
import { ASSIGN_TECHNICIAN_LABELS as L } from '../../constants/labels';
import { SERVICE_MANAGEMENT_LABELS as TL } from '../../constants/translations/labels';
import type {
  AssigneeType,
  EligibleTechnician,
} from '../../types/assignTechnicianType';

const PAGE_LIMIT = 10;

export interface EligibleTechnicianDropdownProps {
  /** Service request UUID. Pagination resets when this changes. */
  serviceRequestId: string | undefined;
  /** ISO date (YYYY-MM-DD) — Europe/Warsaw wall-clock. */
  startDate: string;
  /** ISO date (YYYY-MM-DD) — Europe/Warsaw wall-clock. */
  endDate: string;
  /** HH:mm — defaults applied by BE. */
  startTime: string;
  /** HH:mm — defaults applied by BE. */
  endTime: string;
  /** When true the window is complete + valid; otherwise the dropdown is disabled. */
  windowReady: boolean;
  /** Currently selected technician id (technicians.id UUID). */
  selectedId: string;
  /**
   * Display name for `selectedId`, known by the caller before this
   * component's own fetch resolves (e.g. carried over from a prior mount).
   * Seeds the trigger so the picked technician doesn't blank out when the
   * component remounts — e.g. navigating from the confirm step back to the
   * form step — while the eligible-technicians list re-fetches.
   */
  selectedName?: string;
  /** Display technician code for `selectedId`, paired with `selectedName`. */
  selectedCode?: string;
  /** Fires with the picked technicians.id, the display technician code (e.g. TECH0001), and the technician's display name. */
  onSelect: (id: string, technicianCode: string, name?: string) => void;
  /** Trigger field label. */
  label: ReactNode;
  /** Disable the dropdown entirely (e.g. while submitting). */
  disabled?: boolean;
  /** Error state. */
  error?: boolean;
  /** Error message rendered below the trigger. */
  errorMessage?: string;
  /** Helper text shown below the trigger when there's no error. */
  supportingText?: string;
  /** Fires when the dropdown menu opens or closes. */
  onOpenChange?: (open: boolean) => void;
  /**
   * When false, technicians who previously rejected this SR are shown.
   * Pass false in the reschedule flow — rejection was for the old window.
   * Defaults to true (assign flow excludes rejected technicians).
   */
  excludeRejected?: boolean;
  /**
   * When true, menu items render the technician id wrapped in `[...]` and
   * the trigger displays `name [id]` (name truncates to `...[id]` when long).
   * Defaults to the legacy `(id)` parens style used by the assign flow.
   */
  showCodeAsBracket?: boolean;
  /**
   * Which kind of assignee to list (RR-912). `'admin'` lists eligible admins via
   * `/eligible-assignees?type=admin` and swaps the technician-specific copy/test
   * id for admin equivalents. Defaults to `'technician'` (unchanged behavior).
   */
  assigneeType?: AssigneeType;
}

const EligibleTechnicianDropdownComponent =
  function EligibleTechnicianDropdown({
    serviceRequestId,
    startDate,
    endDate,
    startTime,
    endTime,
    windowReady,
    selectedId,
    selectedName,
    selectedCode,
    onSelect,
    label,
    disabled = false,
    error = false,
    errorMessage,
    supportingText,
    onOpenChange,
    showCodeAsBracket = false,
    excludeRejected = true,
    assigneeType = 'technician',
  }: EligibleTechnicianDropdownProps) {
    const isAdmin = assigneeType === 'admin';
    const triggerTestId = `assign-${assigneeType}-trigger`;
    const searchPlaceholder = isAdmin
      ? L.ADMIN_SEARCH_PLACEHOLDER
      : L.SEARCH_PLACEHOLDER;
    const loadingLabel = isAdmin ? L.ADMIN_LOADING : L.TECHNICIAN_LOADING;
    const noResultsLabel = isAdmin
      ? L.ADMIN_NO_RESULTS
      : L.TECHNICIAN_NO_RESULTS;
    const noWindowTooltip = TL.TECHNICIAN_TOOLTIP_NO_WINDOW.polish;
    const [technicians, setTechnicians] = useState<EligibleTechnician[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [rawSearch, setRawSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    // Incremented each time the dropdown opens to force a fresh fetch even when
    // all other deps (dates, search) are unchanged — clears stale errors and data.
    const openCountRef = useRef(0);
    const [refreshKey, setRefreshKey] = useState(0);
    // Cache the currently-selected option so the trigger keeps showing its name
    // while the list is cleared + refetched on reopen (prevents the value from
    // flickering blank then back). Kept in sync with `selectedId` below.
    const [lastSelected, setLastSelected] = useState<EligibleTechnician | null>(
      () =>
        selectedId && selectedName
          ? {
              id: selectedId,
              technician_id: selectedCode ?? '',
              name: selectedName,
              phone_number: null,
              profile: null,
            }
          : null
    );

    // On open: reset stale search + error state and force a fresh fetch.
    const handleOpenChange = useCallback(
      (isOpen: boolean) => {
        if (isOpen) {
          openCountRef.current += 1;
          setRawSearch('');
          setDebouncedSearch('');
          setFetchError('');
          setRefreshKey(openCountRef.current);
        }
        onOpenChange?.(isOpen);
      },
      [onOpenChange]
    );

    // Debounce search input: 500 ms delay, max 200 chars.
    useEffect(() => {
      if (!rawSearch) {
        setDebouncedSearch('');
        return;
      }
      const id = setTimeout(() => {
        setDebouncedSearch(rawSearch.slice(0, 200));
      }, 500);
      return () => clearTimeout(id);
    }, [rawSearch]);

    // Reset + fetch page 1 whenever the window, SR, or debounced search changes.
    useEffect(() => {
      if (!windowReady || !serviceRequestId) {
        setTechnicians([]);
        setPage(1);
        setHasMore(true);
        setLoading(false);
        setFetchError('');
        return;
      }

      let mounted = true;
      const fetchFirstPage = async () => {
        setLoading(true);
        setTechnicians([]);
        setFetchError('');
        const firstPageParams = {
          serviceRequestId,
          startDate,
          endDate,
          startTime,
          endTime,
          page: 1,
          limit: PAGE_LIMIT,
        };
        if (debouncedSearch) {
          Object.assign(firstPageParams, { search: debouncedSearch });
        }
        if (!excludeRejected) {
          Object.assign(firstPageParams, { excludeRejected: false });
        }
        if (isAdmin) {
          Object.assign(firstPageParams, { assigneeType: 'admin' });
        }
        const result = await listEligibleTechnicians(firstPageParams);
        if (!mounted) {
          return;
        }
        if (result.success) {
          setTechnicians(result.data ?? []);
          setHasMore(result.hasNextPage ?? false);
          setPage(1);
        } else {
          setTechnicians([]);
          setHasMore(false);
          setFetchError(result.error ?? '');
        }
        setLoading(false);
      };
      void fetchFirstPage();
      return () => {
        mounted = false;
      };
    }, [
      serviceRequestId,
      startDate,
      endDate,
      startTime,
      endTime,
      windowReady,
      debouncedSearch,
      excludeRejected,
      isAdmin,
      refreshKey,
    ]);

    const fetchMore = useCallback(async () => {
      if (loading || !hasMore || !serviceRequestId || !windowReady) {
        return;
      }
      setLoading(true);
      const nextPage = page + 1;
      const moreParams = {
        serviceRequestId,
        startDate,
        endDate,
        startTime,
        endTime,
        page: nextPage,
        limit: PAGE_LIMIT,
      };
      if (debouncedSearch) {
        Object.assign(moreParams, { search: debouncedSearch });
      }
      if (!excludeRejected) {
        Object.assign(moreParams, { excludeRejected: false });
      }
      if (isAdmin) {
        Object.assign(moreParams, { assigneeType: 'admin' });
      }
      const result = await listEligibleTechnicians(moreParams);
      if (result.success) {
        setTechnicians((prev) => {
          const incoming = result.data ?? [];
          const dedup = incoming.filter(
            (t) => !prev.some((existing) => existing.id === t.id)
          );
          return [...prev, ...dedup];
        });
        setHasMore(result.hasNextPage ?? false);
        setPage(nextPage);
      } else {
        // Stop attempting further pages on error; keep what we have.
        setHasMore(false);
        setFetchError(result.error ?? '');
      }
      setLoading(false);
    }, [
      loading,
      hasMore,
      page,
      serviceRequestId,
      windowReady,
      startDate,
      endDate,
      startTime,
      endTime,
      debouncedSearch,
      excludeRejected,
      isAdmin,
    ]);

    const items: MenuItemProps[] = technicians.map((t) => ({
      id: t.id,
      label: showCodeAsBracket
        ? `${t.name} [${t.technician_id}]`
        : `${t.name} (${t.technician_id})`,
      toolTip: false as const,
    }));

    if (loading) {
      items.push({
        id: '__loading__',
        label: loadingLabel,
        disabled: true,
      });
    } else if (fetchError && technicians.length === 0) {
      items.push({
        id: '__error__',
        label: fetchError,
        disabled: true,
      });
    } else if (!loading && technicians.length === 0 && windowReady) {
      items.push({
        id: '__empty__',
        label: noResultsLabel,
        disabled: true,
      });
    }

    // Keep the cached selection in sync: refresh it when the selected row is
    // present in the fetched list, and clear it when the selection is removed
    // upstream.
    useEffect(() => {
      if (!selectedId) {
        setLastSelected(null);
        return;
      }
      const found = technicians.find((t) => t.id === selectedId);
      if (found) {
        setLastSelected(found);
      }
    }, [technicians, selectedId]);

    // Prefer the freshly-fetched row, but fall back to the cached selection so
    // the trigger never blanks out during a refetch.
    const selected =
      technicians.find((t) => t.id === selectedId) ??
      (selectedId && lastSelected?.id === selectedId ? lastSelected : null);

    const showNoWindowTooltip = !disabled && !windowReady;

    const dropdownEl = (
      <Dropdown
        menuFullWidth
        toolTip={false}
        showSearch
        searchPlaceholder={searchPlaceholder}
        menuMaxVisibleItems={5}
        autoMaxHeight
        onSearchChange={setRawSearch}
        onOpenChange={handleOpenChange}
        items={items}
        {...(selectedId ? { selectedId } : {})}
        disabled={disabled || !windowReady}
        error={error}
        {...(errorMessage ? { errorMessage } : {})}
        {...(!errorMessage && supportingText ? { supportingText } : {})}
        onItemClick={(id) => {
          if (
            id !== '__loading__' &&
            id !== '__empty__' &&
            id !== '__error__'
          ) {
            const tech = technicians.find((t) => t.id === id);
            if (tech) {
              setLastSelected(tech);
            }
            onSelect(id, tech?.technician_id ?? '', tech?.name ?? '');
          }
        }}
        onScrollEnd={() => {
          if (hasMore && !loading) {
            void fetchMore();
          }
        }}
        trigger={({
          open,
          toggle,
          disabled: triggerDisabled,
          error: triggerError,
        }) => {
          const labelFloating = open || !!selected;
          const labelColor = open ? '#2a4455' : '#6a7278';
          return (
            <button
              type="button"
              onClick={triggerDisabled ? undefined : toggle}
              aria-expanded={open}
              aria-haspopup="true"
              aria-disabled={triggerDisabled}
              data-testid={triggerTestId}
              className={`w-full flex items-center justify-between gap-2 bg-transparent outline-none text-left ${
                triggerDisabled
                  ? 'cursor-not-allowed text-neutral-60'
                  : 'cursor-pointer text-neutral-10'
              }`}
            >
              <div
                className="flex-1 min-w-0 relative flex flex-col justify-center"
                style={{ minHeight: 40 }}
              >
                <span
                  className="absolute left-0 pointer-events-none origin-top-left font-normal"
                  style={{
                    color: labelColor,
                    top: labelFloating ? 0 : 10,
                    fontSize: labelFloating ? 12 : 16,
                    lineHeight: labelFloating ? '16px' : '20px',
                    transition:
                      'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                  }}
                >
                  {label}
                </span>
                {selected && (
                  <Tooltip
                    content={
                      showCodeAsBracket
                        ? `${selected.name} [${selected.technician_id}]`
                        : selected.name
                    }
                    side="bottom"
                    align="left"
                    triggerTabIndex={-1}
                  >
                    {showCodeAsBracket ? (
                      <span
                        className="text-neutral-10 flex w-full items-baseline min-w-0"
                        style={{
                          marginTop: 16,
                          fontSize: 14,
                          lineHeight: '20px',
                          fontWeight: 400,
                        }}
                      >
                        <span className="truncate min-w-0">
                          {selected.name}
                        </span>
                        <span className="shrink-0 ml-1">
                          [{selected.technician_id}]
                        </span>
                      </span>
                    ) : (
                      <span
                        className="text-neutral-10 block w-full truncate"
                        style={{
                          marginTop: 16,
                          fontSize: 14,
                          lineHeight: '20px',
                          fontWeight: 400,
                        }}
                      >
                        {selected.name}
                      </span>
                    )}
                  </Tooltip>
                )}
              </div>
              <ChevronDown
                strokeWidth={1.5}
                className={`shrink-0 transition-transform duration-200 ${
                  open ? 'rotate-180' : ''
                } ${triggerError ? 'text-error-40' : ''}`}
              />
            </button>
          );
        }}
      />
    );

    if (showNoWindowTooltip) {
      return (
        <Tooltip
          content={noWindowTooltip}
          forceShow
          side="bottom"
          align="left"
          cursor="not-allowed"
          // Keep this below the date-picker calendar's z-50 portal (both
          // render to document.body) so an open calendar isn't covered.
          zIndex={500}
        >
          {dropdownEl}
        </Tooltip>
      );
    }

    return dropdownEl;
  };

export const EligibleTechnicianDropdown = React.memo(
  EligibleTechnicianDropdownComponent
);
